// @flow

import * as React from 'react';
import { Environment } from '@ta/lithium.context';
import classNames from '@ta/common.classnames';
import styles from './IndicatorDots.css';

/** Component props for <IndicatorDots/> */
type ExternalProps = {|
  /** The number of dots to display */
  numDots: number,
  /** The total number of items that are available to display */
  numItems: number,
  /** The current zero-based index of the item that is being displayed */
  currentIndex: number,
  /** Set white background for Value Proposition MW to use */
  whiteBackground?: boolean,
|};

type Props = {|
  /** Refer to the JSDocs above for ExternalProps */
  ...ExternalProps,
  /** Make the indicator dots display in the opposite direction for rtl POSes. Retrieved from page state */
  rtl: boolean,
|};

/** Component state for <IndicatorDots/> */
type State = {|
  /** Boolean specifying the direction of change of the index */
  directionUp: boolean,
  /** The number of dots to display */
  numDots: number,
  /** The current zero-based index of the item that is being displayed */
  currentIndex: number,
|};

/** Imagine that each item has a corresponding indicator dot that could be displayed numbered from 0 to numItems - 1.
 * This gets the 'window' of indicator dots that should be shown given the current index,
 * number of dots, total number of items, and the scroll direction
 * Assumptions:
 *  - numDots, numItems, currentIndex are all positive integers
 *  - numDots <= numItems
 *  - 0 <= currentIndex < numItems
 * */
export const getDotRange = (
  numDots: number,
  numItems: number,
  currentIndex: number,
  directionUp: boolean,
): Array<number> => {
  const startMin = 0;
  const startMax = numItems - numDots;
  const startAdjustment = directionUp ? 0 : -0.5;
  const startOffset = Math.floor((numDots + startAdjustment) / 2);

  const endMin = numDots - 1;
  const endMax = numItems - 1;
  const endAdjustment = directionUp ? -0.5 : 0;
  const endOffset = Math.floor((numDots + endAdjustment) / 2);

  const start = Math.max(startMin, Math.min(startMax, currentIndex - startOffset));
  const end = Math.min(endMax, Math.max(endMin, currentIndex + endOffset));

  return Array(end - start + 1)
    .fill()
    .map((_, i) => start + i);
};

/** The amount of space in px that should exist between indicator dots */
export const DOT_SPACING = 20;
/** The relative size of the active (highlighted) dot */
export const ACTIVE_DOT_SCALE = 1.3;
/** The relative size of the bookend dots (facilitate dot animation) */
export const BOOKEND_DOT_SCALE = 0;

/** Calculate the transform styling for a given dot */
export const getDotStyle = (position: number, numDots: number, isActive: boolean, rtl: boolean) => {
  const shiftAmount = `${rtl ? '-' : ''}${DOT_SPACING * position}px`;
  const isBookend = position === 0 || position === numDots + 1;
  let scaleAmount;
  if (isBookend) {
    scaleAmount = BOOKEND_DOT_SCALE;
  } else if (isActive) {
    scaleAmount = ACTIVE_DOT_SCALE;
  } else {
    scaleAmount = 1;
  }
  const transform = `translate3d(${shiftAmount}, 0, 0) scale3d(${scaleAmount}, ${scaleAmount}, 1)`;
  return { transform };
};

/** The main indicator dots component, not connected to page state. exported for testing */
export class IndicatorDotsUnconnected extends React.PureComponent<Props, State> {
  static defaultProps = {
    rtl: false,
  };

  static getDerivedStateFromProps(newProps: Props, prevState: State) {
    return {
      directionUp: newProps.currentIndex >= prevState.currentIndex,
      numDots: newProps.numItems < newProps.numDots ? newProps.numItems : newProps.numDots,
      currentIndex: newProps.currentIndex,
    };
  }

  constructor(props: Props) {
    super(props);
    this.state = {
      directionUp: true,
      numDots: props.numItems < props.numDots ? props.numItems : props.numDots,
      currentIndex: props.currentIndex,
    };
  }

  render() {
    const { numItems, rtl } = this.props;
    const { numDots, currentIndex, directionUp } = this.state;

    if (numDots <= 1) {
      return null;
    }

    // dots to display
    const dotRange = getDotRange(numDots, numItems, currentIndex, directionUp);
    // bookend dots that facilitate animation
    dotRange.unshift(dotRange[0] - 1);
    dotRange.push(dotRange[numDots] + 1);

    const dotItems = dotRange.map((positionIndex, i) => {
      const isActive = positionIndex === currentIndex;
      return (
        <div
          key={`indicator-dot-${positionIndex}`}
          className={classNames(styles.dot, isActive ? styles.active : null)}
          style={getDotStyle(i, numDots, isActive, rtl)}
        />
      );
    });

    return (
      <div
        className={classNames(
          styles.indicatorDots,
          this.props.whiteBackground ? null : styles.background,
        )}
      >
        <div
          className={styles.dotWrapper}
          style={{ width: `${(numDots + 1) * DOT_SPACING + 6}px` }}
          data-clicksource="IndicatorDot"
        >
          {dotItems}
        </div>
      </div>
    );
  }
}

const IndicatorDotsConnected = (props: ExternalProps) => {
  const env = React.useContext(Environment);
  return <IndicatorDotsUnconnected {...props} rtl={env.locale.isRtl} />;
};

/** The connected IndicatorDots component */
export default IndicatorDotsConnected;
